// src/pages/PartyLedgerPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import InvoicePopup from "../components/InvoicePopup";
import VoucherPopup from "../components/VoucherPopup";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
export default function PartyLedgerPage() {
  const { partyId } = useParams();
  const [entries, setEntries] = useState([]);
  const [partyDoc, setPartyDoc] = useState(null);
  const [partyName, setPartyName] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedVoucher, setSelectedVoucher] = useState(null);

  // 🔹 Opening balance state
  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingType, setOpeningType] = useState("Dr");
  const [loading, setLoading] = useState(true);

  // UI filters
  const [search, setSearch] = useState("");
  const [entryType, setEntryType] = useState("all");
  const [sideFilter, setSideFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Robust number conversion helper
  function toNumberSafe(v) {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const cleaned = v.replace(/,/g, '').trim();
      const n = parseFloat(cleaned);
      return isNaN(n) ? 0 : n;
    }
    // try convert (e.g. Firestore field wrappers)
    try {
      const n = Number(v);
      return isNaN(n) ? 0 : n;
    } catch (e) {
      return 0;
    }
  }

  function toDateSafe(v) {
    if (!v) return null;
    if (typeof v?.toDate === "function") {
      const d = v.toDate();
      return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    if (v instanceof Date) return !isNaN(v.getTime()) ? v : null;
    const d = new Date(v);
    return !isNaN(d.getTime()) ? d : null;
  }

  function formatDate(v) {
    const d = toDateSafe(v);
    return d ? d.toLocaleDateString("en-IN") : "-";
  }

  function formatDateTime(v) {
    const d = toDateSafe(v);
    return d ? d.toLocaleString("en-IN") : "-";
  }

  function formatAmount(v) {
    return toNumberSafe(v).toFixed(2);
  }

  function formatBalance(v) {
    const n = toNumberSafe(v);
    return n >= 0 ? `${n.toFixed(2)} Dr` : `${Math.abs(n).toFixed(2)} Cr`;
  }

  function ymd(v) {
    const d = toDateSafe(v);
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 🔹 Fetch Party (for opening balance)
        const partySnap = await getDoc(doc(db, "parties", partyId));
        let resolvedPartyName = "";
        if (partySnap.exists()) {
          const pdata = partySnap.data();
          resolvedPartyName = pdata.partyName || "";
          setPartyDoc(pdata);
          setPartyName(resolvedPartyName);
          setOpeningBalance(toNumberSafe(pdata.openingBalance || 0));
          setOpeningType(pdata.openingType || "Dr");
        }

        const { loadSalesImportInvoiceLikeDocs } = await import("../services/salesImportTransactionDataService");
        const importDocs = await loadSalesImportInvoiceLikeDocs({ maxWeeks: 24 });
        const pn = resolvedPartyName.toLowerCase();
        const invoices = importDocs
          .filter((inv) => {
            const ip = String(inv.partyName || inv.party || "").toLowerCase();
            return ip === pn || ip.includes(pn) || pn.includes(ip);
          })
          .map((inv) => ({ ...inv, source: "sales_import" }));

        // 🔹 Vouchers
        const vouQ = query(collection(db, "vouchers"), orderBy("date", "asc"));
        const vouSnap = await getDocs(vouQ);

        let vouchers = [];
        vouSnap.docs.forEach((doc) => {
          const v = doc.data();
          (v.rows || []).forEach((r) => {
            if (r.partyId === partyId) {
              vouchers.push({
                id: doc.id,
                source: "voucher",
                voucherType: v.voucherType,
                voucherNo: v.voucherNo,
                date: v.date,
                narration: v.narration || "",
                drcr: r.drcr,
                debit: toNumberSafe(r.debit),
                credit: toNumberSafe(r.credit),
                created: v.createdAt || null,
                partyName: r.partyName || "",
              });
            }
          });
        });

        // 🔹 Merge
        let allEntries = [];

        invoices.forEach((inv) => {
          const grand = toNumberSafe(inv.grandTotal);
          allEntries.push({
            id: inv.id,
            source: "invoice",
            voucherType: inv.type,
            voucherNo: inv.voucherNo,
            date: inv.date,
            narration: inv.transactionClass || "Invoice Entry",
            debit: inv.type === "inward" ? grand : 0,
            credit: inv.type === "outward" ? grand : 0,
            created: inv.created || null,
            partyName: inv.partyDetails?.partyName || "",
          });
        });

        allEntries = [...allEntries, ...vouchers];

        // 🔹 Sort by date
        allEntries.sort((a, b) => {
          const da = a.date?.toDate ? a.date.toDate() : new Date(a.date);
          const db = b.date?.toDate ? b.date.toDate() : new Date(b.date);
          return da - db;
        });

        setEntries(allEntries);
      } catch (err) {
        console.error("Error fetching ledger:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [partyId]);

  // 🔹 Ledger calculation with opening balance
  const rows = useMemo(() => {
    let runningBalance = openingType === "Dr" ? toNumberSafe(openingBalance) : -toNumberSafe(openingBalance);
    return entries.map((e) => {
      const debit = toNumberSafe(e.debit);
      const credit = toNumberSafe(e.credit);
      runningBalance += debit - credit;
      return { ...e, debit, credit, balance: runningBalance };
    });
  }, [entries, openingBalance, openingType]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (entryType !== "all" && String(r.source) !== entryType) return false;
      if (sideFilter === "debit" && !(toNumberSafe(r.debit) > 0)) return false;
      if (sideFilter === "credit" && !(toNumberSafe(r.credit) > 0)) return false;

      const rowYmd = ymd(r.date);
      if (fromDate && rowYmd && rowYmd < fromDate) return false;
      if (toDate && rowYmd && rowYmd > toDate) return false;

      if (!q) return true;
      const hay = [
        r.voucherType,
        r.voucherNo,
        r.narration,
        r.partyName,
        r.source,
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, search, entryType, sideFilter, fromDate, toDate]);

  const totalDebit = filteredRows.reduce((s, r) => s + toNumberSafe(r.debit), 0) + (openingType === "Dr" ? toNumberSafe(openingBalance) : 0);
  const totalCredit = filteredRows.reduce((s, r) => s + toNumberSafe(r.credit), 0) + (openingType === "Cr" ? toNumberSafe(openingBalance) : 0);
  const closingBalance = totalDebit - totalCredit;
  const invoiceCount = filteredRows.filter((r) => r.source === "invoice").length;
  const voucherCount = filteredRows.filter((r) => r.source === "voucher").length;

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <MenuBar />
      <HeaderBar title={`Ledger - ${partyName || partyId}`} />

      <div className="mx-auto flex h-full w-[95%] flex-col gap-4 py-4 min-h-0">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-lg font-semibold mb-1 text-slate-900">
            Transactions for {partyName || partyId}
          </h3>
          {partyDoc?.phone || partyDoc?.gst || partyDoc?.address ? (
            <p className="text-xs text-slate-600">
              {partyDoc?.phone ? `Phone: ${partyDoc.phone}` : ""}{partyDoc?.phone && partyDoc?.gst ? "  |  " : ""}
              {partyDoc?.gst ? `GST: ${partyDoc.gst}` : ""}{(partyDoc?.phone || partyDoc?.gst) && partyDoc?.address ? "  |  " : ""}
              {partyDoc?.address ? `Address: ${partyDoc.address}` : ""}
            </p>
          ) : null}
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3 lg:grid-cols-6">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Opening</p>
              <p className="mt-0.5 font-bold text-slate-900">{formatBalance(openingType === "Dr" ? openingBalance : -openingBalance)}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
              <p className="text-[10px] text-emerald-700 uppercase tracking-wide">Debit Total</p>
              <p className="mt-0.5 font-bold text-emerald-900">{formatAmount(totalDebit)}</p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
              <p className="text-[10px] text-rose-700 uppercase tracking-wide">Credit Total</p>
              <p className="mt-0.5 font-bold text-rose-900">{formatAmount(totalCredit)}</p>
            </div>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
              <p className="text-[10px] text-indigo-700 uppercase tracking-wide">Closing</p>
              <p className="mt-0.5 font-bold text-indigo-900">{formatBalance(closingBalance)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Invoices</p>
              <p className="mt-0.5 font-bold text-slate-900">{invoiceCount}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Vouchers</p>
              <p className="mt-0.5 font-bold text-slate-900">{voucherCount}</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-6">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search no/type/narration..."
              className="h-9 rounded-lg border border-slate-300 px-3 text-sm lg:col-span-2"
            />
            <select value={entryType} onChange={(e) => setEntryType(e.target.value)} className="h-9 rounded-lg border border-slate-300 px-2 text-sm">
              <option value="all">All entries</option>
              <option value="invoice">Invoices</option>
              <option value="voucher">Vouchers</option>
            </select>
            <select value={sideFilter} onChange={(e) => setSideFilter(e.target.value)} className="h-9 rounded-lg border border-slate-300 px-2 text-sm">
              <option value="all">Debit + Credit</option>
              <option value="debit">Only Debit</option>
              <option value="credit">Only Credit</option>
            </select>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-lg border border-slate-300 px-2 text-sm" />
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-lg border border-slate-300 px-2 text-sm" />
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setEntryType("all");
                setSideFilter("all");
                setFromDate("");
                setToDate("");
              }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Reset filters
            </button>
          </div>
        </div>

        <div className="w-full bg-white shadow-sm rounded-2xl p-0 border border-slate-200 flex flex-col min-h-0 flex-1 overflow-hidden">
        {loading ? (
          <p className="p-4 text-sm text-slate-600">Loading ledger...</p>
        ) : filteredRows.length === 0 && toNumberSafe(openingBalance) === 0 ? (
          <p className="p-4 text-sm text-rose-600">No ledger entries found for this party.</p>
        ) : (
          <div className="flex flex-col h-full min-h-0">
            <div className="overflow-auto">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                  <tr>
                    <th className="p-2 border border-slate-200 text-left">Type</th>
                    <th className="p-2 border border-slate-200 text-left">No</th>
                    <th className="p-2 border border-slate-200 text-left">Date</th>
                    <th className="p-2 border border-slate-200 text-left">Narration</th>
                    <th className="p-2 border border-slate-200 text-right">Debit</th>
                    <th className="p-2 border border-slate-200 text-right">Credit</th>
                    <th className="p-2 border border-slate-200 text-right">Balance</th>
                    <th className="p-2 border border-slate-200 text-left">Entry Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-amber-50 font-semibold">
                    <td className="p-2 border border-slate-200">Opening</td>
                    <td className="p-2 border border-slate-200">-</td>
                    <td className="p-2 border border-slate-200">{fromDate || "-"}</td>
                    <td className="p-2 border border-slate-200">Balance b/d</td>
                    <td className="p-2 border border-slate-200 text-right">{openingType === "Dr" ? formatAmount(openingBalance) : "-"}</td>
                    <td className="p-2 border border-slate-200 text-right">{openingType === "Cr" ? formatAmount(openingBalance) : "-"}</td>
                    <td className="p-2 border border-slate-200 text-right">{formatBalance(openingType === "Dr" ? openingBalance : -openingBalance)}</td>
                    <td className="p-2 border border-slate-200">-</td>
                  </tr>

                  {filteredRows.map((r, i) => (
                    <tr key={`${r.source}-${r.id}-${i}`} className={i % 2 === 0 ? "bg-white hover:bg-slate-50" : "bg-slate-50/50 hover:bg-slate-100/60"}>
                      <td className="p-2 border border-slate-200">{r.voucherType || "-"}</td>
                      <td
                        className="p-2 border border-slate-200 text-blue-600 underline cursor-pointer hover:text-blue-800"
                        onDoubleClick={() => {
                          if (r.source === "invoice") setSelectedInvoice(r.id);
                          if (r.source === "voucher") setSelectedVoucher(r.id);
                        }}
                        title="Double click to open"
                      >
                        {r.voucherNo || "-"}
                      </td>
                      <td className="p-2 border border-slate-200">{formatDate(r.date)}</td>
                      <td className="p-2 border border-slate-200 max-w-[280px] truncate" title={r.narration || ""}>{r.narration || "-"}</td>
                      <td className="p-2 border border-slate-200 text-right">{formatAmount(r.debit)}</td>
                      <td className="p-2 border border-slate-200 text-right">{formatAmount(r.credit)}</td>
                      <td className="p-2 border border-slate-200 text-right font-semibold">{formatBalance(r.balance)}</td>
                      <td className="p-2 border border-slate-200">{formatDateTime(r.created)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-yellow-100 font-semibold">
                    <td className="p-2 border border-slate-200" colSpan={4}>Closing balance</td>
                    <td className="p-2 border border-slate-200 text-right">{formatAmount(totalDebit)}</td>
                    <td className="p-2 border border-slate-200 text-right">{formatAmount(totalCredit)}</td>
                    <td className="p-2 border border-slate-200 text-right">{formatBalance(closingBalance)}</td>
                    <td className="p-2 border border-slate-200">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Popups */}
      {selectedInvoice && (
        <InvoicePopup
          invoiceId={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
      {selectedVoucher && (
        <VoucherPopup
          voucherId={selectedVoucher}
          onClose={() => setSelectedVoucher(null)}
        />
      )}
    </div>
  );
}
